import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  Close,
  ChevronLeft,
  ChevronRight,
  Download,
  ZoomIn,
  ZoomOut,
} from "@mui/icons-material";
import { urlDeArchivo } from "../../config";
import "./lightbox.scss";

/**
 * Visor de imágenes y videos a pantalla completa.
 *
 * ---------------------------------------------------------------------------
 * POR QUÉ EXISTE
 * ---------------------------------------------------------------------------
 * Antes, al hacer clic en una foto de la galería de un espacio se abría un
 * recuadro que dejaba la página de fondo casi visible y mostraba la imagen a
 * un 80% del alto de la ventana, con el botón de cerrar montado sobre la
 * barra superior. En una foto de 1600px de ancho se veía más pequeña que en
 * cualquier visor del sistema operativo, así que no servía para lo único que
 * se le pide: mirar bien la imagen.
 *
 * Las fotos de las publicaciones del muro directamente no se podían ampliar.
 *
 * Este componente reemplaza los dos casos:
 *   - fondo casi negro, para que nada compita con la foto
 *   - la imagen ocupa todo el espacio disponible
 *   - flechas y teclado para pasar entre las fotos del mismo grupo
 *   - acercar para ver el detalle de una imagen grande
 *   - descargar, y cerrar con Escape o clic fuera
 *
 * ---------------------------------------------------------------------------
 * CÓMO SE USA
 * ---------------------------------------------------------------------------
 *   <Lightbox
 *     items={[{ url, titulo, descripcion }]}   // o un array de strings
 *     indice={i}
 *     onIndice={setI}
 *     onCerrar={() => setAbierto(false)}
 *   />
 *
 * Se monta con createPortal directamente en <body>: varios contenedores de la
 * aplicación llevan `overflow: hidden` y bordes redondeados, y un visor que
 * viva dentro de ellos acaba recortado.
 */

const esVideo = (url = "") =>
  /\.(mp4|webm|mov|avi|mkv|3gp|3g2|ogv|wmv|mpe?g)$/i.test(String(url).split("?")[0]);

/** Acepta tanto ["foto.jpg"] como [{ url, titulo }] para no obligar a los que ya pasan strings. */
const normalizar = (item) =>
  typeof item === "string"
    ? { url: item, titulo: "", descripcion: "" }
    : {
        url: item?.url || item?.imageUrl || item?.fileUrl || "",
        titulo: item?.titulo || item?.title || item?.fileName || "",
        descripcion: item?.descripcion || item?.description || "",
      };

const Lightbox = ({ items = [], indice = 0, onIndice, onCerrar }) => {
  const [acercada, setAcercada] = useState(false);

  const lista = (Array.isArray(items) ? items : [items]).map(normalizar).filter((i) => i.url);
  const total = lista.length;
  const actual = lista[Math.min(Math.max(indice, 0), total - 1)];

  const irA = useCallback(
    (siguiente) => {
      if (total < 2 || !onIndice) return;
      setAcercada(false);
      onIndice((siguiente + total) % total);
    },
    [total, onIndice]
  );

  // Teclado: Escape cierra, flechas navegan. Es lo que espera cualquiera que
  // haya usado un visor de fotos antes.
  useEffect(() => {
    const alPulsar = (e) => {
      if (e.key === "Escape") onCerrar?.();
      else if (e.key === "ArrowLeft") irA(indice - 1);
      else if (e.key === "ArrowRight") irA(indice + 1);
    };
    window.addEventListener("keydown", alPulsar);
    return () => window.removeEventListener("keydown", alPulsar);
  }, [indice, irA, onCerrar]);

  // Mientras el visor está abierto, la página de detrás no debe desplazarse:
  // si no, al mover la rueda para nada la página se va moviendo por debajo.
  useEffect(() => {
    const anterior = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = anterior;
    };
  }, []);

  if (!total || !actual) return null;

  const video = esVideo(actual.url);

  return createPortal(
    <div className="lightbox" onClick={onCerrar} role="dialog" aria-modal="true">
      <div className="lightbox__barra" onClick={(e) => e.stopPropagation()}>
        <span className="lightbox__titulo">
          {actual.titulo}
          {total > 1 && (
            <span className="lightbox__contador">
              {indice + 1} de {total}
            </span>
          )}
        </span>

        <div className="lightbox__acciones">
          {!video && (
            <button
              type="button"
              onClick={() => setAcercada((v) => !v)}
              title={acercada ? "Ajustar a la pantalla" : "Ver a tamaño real"}
              aria-label={acercada ? "Ajustar a la pantalla" : "Ver a tamaño real"}
            >
              {acercada ? <ZoomOut /> : <ZoomIn />}
            </button>
          )}
          <a
            href={urlDeArchivo(actual.url)}
            download
            target="_blank"
            rel="noopener noreferrer"
            title="Descargar"
            aria-label="Descargar"
          >
            <Download />
          </a>
          <button type="button" onClick={onCerrar} title="Cerrar" aria-label="Cerrar">
            <Close />
          </button>
        </div>
      </div>

      {total > 1 && (
        <>
          <button
            type="button"
            className="lightbox__nav lightbox__nav--prev"
            onClick={(e) => {
              e.stopPropagation();
              irA(indice - 1);
            }}
            aria-label="Anterior"
          >
            <ChevronLeft />
          </button>
          <button
            type="button"
            className="lightbox__nav lightbox__nav--next"
            onClick={(e) => {
              e.stopPropagation();
              irA(indice + 1);
            }}
            aria-label="Siguiente"
          >
            <ChevronRight />
          </button>
        </>
      )}

      <div
        className={`lightbox__lienzo ${acercada ? "acercada" : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        {video ? (
          <video src={urlDeArchivo(actual.url)} controls autoPlay controlsList="nodownload" />
        ) : (
          <img
            src={urlDeArchivo(actual.url)}
            alt={actual.titulo || "Imagen ampliada"}
            onClick={() => setAcercada((v) => !v)}
          />
        )}
      </div>

      {actual.descripcion && (
        <p className="lightbox__pie" onClick={(e) => e.stopPropagation()}>
          {actual.descripcion}
        </p>
      )}
    </div>,
    document.body
  );
};

export default Lightbox;
